/**
 * `main.tsx` — Vite/React entry point (Req 3.1).
 *
 * Locates the `#root` element declared in `index.html`, creates a React 18
 * concurrent root, and renders the application shell inside the fixed
 * provider tree defined by `providers/AppProviders.tsx`.
 *
 *   <React.StrictMode>
 *     <AppProviders>   ← HelmetProvider → QueryClientProvider → BrowserRouter
 *       <App />        ← Navbar + routed outlet + Footer
 *     </AppProviders>
 *   </React.StrictMode>
 *
 * `AppProviders` is the single composition seam for cross-cutting providers
 * (Req 16.7); this file should never grow to host provider wiring. If the
 * `#root` element is missing (misconfigured `index.html` or wrong mount
 * target), we throw eagerly instead of silently no-oping — this surfaces
 * the misconfiguration in the browser console rather than shipping a
 * blank page.
 *
 * Requirements: 3.1.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';

import App from './App';
import { AppProviders } from './providers/AppProviders';
import './styles/globals.css';

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppProviders>
      <App />
    </AppProviders>
  </React.StrictMode>,
);
